import { z } from "zod";
import { blockSchema } from "../blocks.js";

/**
 * Prior turns' user/assistant text — deliberately *not* the full
 * `ProviderMessage` union (no `tool`-role replay across turns). The test
 * console is stateless: nothing is persisted, so each request carries the
 * whole visible transcript back. Replaying resolved tool text is enough for
 * the model to stay coherent across turns; replaying raw tool-call/result
 * pairs from earlier turns isn't needed for "is this one reply good" testing
 * and would otherwise make this schema mirror the internal `ProviderMessage`
 * union exactly.
 */
export const testConsoleMessageSchema = z.object({
  role: z.enum(["user", "assistant"]),
  content: z.string().min(1),
});
export type TestConsoleMessage = z.infer<typeof testConsoleMessageSchema>;

export const runTestTurnBodySchema = z.object({
  history: z.array(testConsoleMessageSchema).max(50).default([]),
  message: z.string().min(1),
});
export type RunTestTurnBody = z.infer<typeof runTestTurnBodySchema>;

/** One `search_products`/`present_products`/etc. call from the trace pane — args, result, latency and cost, per PLAN.md "Test console". */
export const testToolCallTraceSchema = z.object({
  name: z.string(),
  args: z.unknown(),
  result: z.unknown(),
  costUsd: z.string().optional(),
  latencyMs: z.number(),
});
export type TestToolCallTrace = z.infer<typeof testToolCallTraceSchema>;

export const testReplySchema = z.object({
  content: z.string().nullable(),
  blocks: z.array(blockSchema).nullable(),
  model: z.string().optional(),
  tokensIn: z.number(),
  tokensOut: z.number(),
  costUsd: z.string().optional(),
  latencyMs: z.number(),
});
export type TestReply = z.infer<typeof testReplySchema>;

/**
 * The full trace pane for one test turn (PLAN.md "Test console... a full
 * trace pane: the resolved system prompt, every tool call with
 * args/results/latency/tokens/cost, and per-turn cost"). Per-turn cost is
 * `reply.costUsd` — `runTurn` already accumulates every provider call and
 * billable tool call onto the turn's final message (see M3's cost
 * accounting), so it isn't duplicated here as a separate field.
 */
export const runTestTurnResponseSchema = z.object({
  systemPrompt: z.string(),
  outcome: z.enum(["completed", "blocked", "aborted", "error"]),
  /** `undefined` only when the turn ended `blocked`/`aborted` before a reply was ever produced. */
  reply: testReplySchema.optional(),
  toolCalls: z.array(testToolCallTraceSchema),
});
export type RunTestTurnResponse = z.infer<typeof runTestTurnResponseSchema>;
